// Fund / rice cascade — single source of truth for Fund Management (desktop +
// mobile), the Monthly Ledger, MDCF auto-fill, Rice Running Report, Daily
// Consumption and Quarterly UC.
//
// Anchor = the school's earliest saved fund record; only the anchor's stored
// opening is used. Every later month (saved or not) is computed:
//   opening  = previous month's closing
//   received = saved receivedFund / receivedRice (rice receipts + stock
//              adjustments are already folded into receivedRice); 0 if unsaved
//   consumed = consumedForMonth() from that month's Daily Entries
//   closing  = opening + received - consumed
// Months before the anchor → null. Stored openings on non-anchor months are ignored.

import { dailyApi, fundApi } from "./setupApi";
import { splitConsumed } from "./consumption";
import { ensureRateCards } from "./useRateCardsSync";

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const MAX_MONTHS = 120;

const empty = () => ({ balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 });
const ym = (y, m) => y * 12 + (m - 1);
const fromYm = (n) => ({ year: Math.floor(n / 12), month: (n % 12) + 1 });

// Pure: consumption from an already-loaded list of a month's entries.
// { cost, rice } = MDM + DR total; mdm / dr carry the split.
export function consumedFromEntries(entries) {
  const sp = splitConsumed(entries);
  return { cost: sp.total.cost, rice: sp.total.rice, mdm: sp.mdm, dr: sp.dr, entryCount: (entries || []).length };
}

export async function consumedForMonth(schoolId, year, month) {
  const last = new Date(year, month, 0).getDate();
  const f = `${year}-${String(month).padStart(2, "0")}-01`;
  const t = `${year}-${String(month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
  const [entries] = await Promise.all([dailyApi.list({ schoolId, fromDate: f, toDate: t }).catch(() => []), ensureRateCards()]);
  return consumedFromEntries(entries);
}

// Ledger shape of a consumed result: { fund, rice, mdm: {fund, rice}, dr: {fund, rice} }.
export function ledgerConsumed(cons) {
  const z = empty();
  return {
    fund: cons.cost, rice: cons.rice,
    mdm: { fund: cons.mdm?.cost || cons.cost, rice: cons.mdm?.rice || cons.rice },
    dr: { fund: cons.dr?.cost || z, rice: cons.dr?.rice || z },
  };
}

async function savedRecords(schoolId, cache) {
  if (!cache.has("__records")) {
    const list = await fundApi.list(schoolId).catch(() => []);
    const sorted = [...(list || [])].sort((a, b) => ym(a.year, a.month) - ym(b.year, b.month));
    cache.set("__records", sorted);
  }
  return cache.get("__records");
}

// Starting month = earliest record whose opening the user entered
// (openingEntered); legacy data: earliest record not auto-created by a
// receipt / adjustment.
export function pickAnchor(records) {
  return records.find((r) => r.openingEntered) || records.find((r) => !r.autoCreated) || null;
}

// Full cascaded row for (year, month), or null before the anchor / no records.
export async function cascadeMonth(schoolId, year, month, cache = new Map()) {
  const key = `${year}-${month}`;
  if (cache.has(key)) return cache.get(key);
  const records = await savedRecords(schoolId, cache);
  const anchor = pickAnchor(records);
  const target = ym(year, month);
  if (!anchor || target < ym(anchor.year, anchor.month) || target - ym(anchor.year, anchor.month) > MAX_MONTHS) {
    cache.set(key, null);
    return null;
  }
  const byKey = new Map(records.map((r) => [`${r.year}-${r.month}`, r]));
  let prev = null;
  for (let n = ym(anchor.year, anchor.month); n <= target; n += 1) {
    const { year: y, month: m } = fromYm(n);
    const k = `${y}-${m}`;
    if (cache.has(k)) { prev = cache.get(k); continue; }
    const rec = byKey.get(k);
    const isAnchor = n === ym(anchor.year, anchor.month);
    const opening = isAnchor
      ? { fund: { ...empty(), ...(anchor.openingFund || {}) }, rice: { ...empty(), ...(anchor.openingRice || {}) } }
      : { fund: { ...prev.closing.fund }, rice: { ...prev.closing.rice } };
    const received = {
      fund: { ...empty(), ...(rec?.receivedFund || {}) },
      rice: { ...empty(), ...(rec?.receivedRice || {}) },
    };
    // eslint-disable-next-line no-await-in-loop
    const cons = cache.get(`__cons:${k}`) || await consumedForMonth(schoolId, y, m);
    const closing = { fund: empty(), rice: empty() };
    CLASS_KEYS.forEach((c) => {
      closing.fund[c] = Number(((Number(opening.fund[c]) || 0) + (Number(received.fund[c]) || 0) - (cons.cost[c] || 0)).toFixed(2));
      closing.rice[c] = Number(((Number(opening.rice[c]) || 0) + (Number(received.rice[c]) || 0) - (cons.rice[c] || 0)).toFixed(3));
    });
    prev = {
      year: y, month: m, saved: !!rec, isAnchor, entryCount: cons.entryCount,
      opening, received, consumed: ledgerConsumed(cons), closing,
    };
    cache.set(k, prev);
  }
  return prev;
}

export async function cascadeClosing(schoolId, year, month, cache = new Map()) {
  const row = await cascadeMonth(schoolId, year, month, cache);
  return row ? { fund: { ...row.closing.fund }, rice: { ...row.closing.rice } } : null;
}

export async function cascadeOpening(schoolId, year, month, cache = new Map()) {
  const row = await cascadeMonth(schoolId, year, month, cache);
  return row ? { fund: { ...row.opening.fund }, rice: { ...row.opening.rice } } : null;
}

// Every month from the anchor to the latest month with data (last saved
// record, or a later month up to today that has Daily Entries), saved or not.
// Returns an ordered Map keyed `${year}-${month}`.
export async function buildFullLedger(schoolId, balances, cache = new Map()) {
  if (balances) cache.set("__records", [...balances].sort((a, b) => ym(a.year, a.month) - ym(b.year, b.month)));
  const records = await savedRecords(schoolId, cache);
  const ledger = new Map();
  const anchor = pickAnchor(records);
  if (!anchor) return ledger;
  const start = ym(anchor.year, anchor.month);
  const lastSaved = ym(records[records.length - 1].year, records[records.length - 1].month);
  const now = new Date();
  const today = Math.min(ym(now.getFullYear(), now.getMonth() + 1), start + MAX_MONTHS);
  for (let n = start; n <= Math.max(lastSaved, today); n += 1) {
    const { year, month } = fromYm(n);
    // eslint-disable-next-line no-await-in-loop
    const row = await cascadeMonth(schoolId, year, month, cache);
    if (row && (n <= lastSaved || row.entryCount > 0)) ledger.set(`${year}-${month}`, row);
  }
  return ledger;
}
